import { diffProfile, type ProfileContent } from "@/lib/profileDraft";

type Props = {
  live: ProfileContent;
  proposed: ProfileContent;
  liveChangedSinceDraft?: boolean;
  approvalValid?: boolean;
  /** Whose voice the copy uses. */
  audience?: "athlete" | "admin";
};

const KIND_LABEL = { added: "New", removed: "Removed", changed: "Updated" } as const;

/** Current live profile vs SIIS's proposed profile, in plain language. */
export const ProfileDraftComparison = ({ live, proposed, liveChangedSinceDraft, approvalValid, audience = "athlete" }: Props) => {
  const changes = diffProfile(live, proposed);
  const admin = audience === "admin";
  return (
    <section aria-labelledby="draft-compare-title" className="rounded-2xl border border-border bg-card p-5 space-y-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">{admin ? "Live profile vs private draft" : "Review proposed changes"}</p>
        <h2 id="draft-compare-title" className="font-display text-xl mt-1">
          {changes.length === 0 ? "No changes proposed yet" : `${admin ? "Draft proposes" : "SIIS proposes"} ${changes.length} change${changes.length === 1 ? "" : "s"}`}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {admin ? "Her public profile stays exactly as it is until she approves this exact version and an admin launches it."
            : "Your public profile stays exactly as it is until you approve this version and SIIS launches it. Only SIIS can publish or launch changes."}
        </p>
      </div>
      {liveChangedSinceDraft && (
        <p role="alert" className="rounded-xl bg-destructive/10 text-destructive text-sm p-3">
          {admin ? "Her live profile changed after this draft was started. Re-base the draft before she can approve it or it can be launched."
            : "Your live profile changed after this draft was started. SIIS needs to update the draft before it can be approved or launched."}
        </p>
      )}
      {approvalValid && <p className="rounded-xl bg-muted text-sm p-3">{admin ? "She approved this exact version." : "You approved this exact version."}</p>}
      <ul className="divide-y divide-border">
        {changes.map((c) => (
          <li key={c.key} className="py-3 grid gap-2 sm:grid-cols-[10rem_1fr_1fr] sm:gap-4">
            <div>
              <p className="font-semibold text-sm">{c.label}</p>
              <span className="text-xs font-medium text-primary">{KIND_LABEL[c.kind]}</span>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Live now</p>
              <p className="text-sm whitespace-pre-line">{c.before || <span className="italic text-muted-foreground">Not shown</span>}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Proposed</p>
              <p className="text-sm whitespace-pre-line">{c.after || <span className="italic text-muted-foreground">Removed</span>}</p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default ProfileDraftComparison;
